import React, { memo } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import { X } from 'lucide-react';

export const NODE_TYPES_CONFIG = {
  // Input nodes
  market_data: {
    label: 'Market Data',
    description: 'Fetch live prices, bars, or orderbook data for a symbol',
    icon: '📊',
    color: 'border-blue-500/30 bg-blue-500/5',
    category: 'input',
    defaultConfig: { symbol: '', dataType: 'price' },
  },
  strategy_status: {
    label: 'Strategy Status',
    description: 'Get performance, positions, and P&L for a strategy',
    icon: '📋',
    color: 'border-blue-500/30 bg-blue-500/5',
    category: 'input',
    defaultConfig: { strategyId: '' },
  },
  timer: {
    label: 'Timer',
    description: 'Trigger this branch on a recurring interval',
    icon: '⏱️',
    color: 'border-blue-500/30 bg-blue-500/5',
    category: 'input',
    defaultConfig: { intervalMinutes: 5 },
  },

  // Analysis nodes
  ai_analysis: {
    label: 'AI Analysis',
    description: 'Send upstream data to Claude for AI-powered analysis',
    icon: '🧠',
    color: 'border-purple-500/30 bg-purple-500/5',
    category: 'analysis',
    defaultConfig: { prompt: 'Analyze the current market conditions', model: 'sonnet' },
  },
  indicator_check: {
    label: 'Indicator Check',
    description: 'Calculate a technical indicator (ATR, RSI, SMA, etc.)',
    icon: '📈',
    color: 'border-purple-500/30 bg-purple-500/5',
    category: 'analysis',
    defaultConfig: { indicator: 'atr', period: 14 },
  },

  // Condition nodes
  threshold: {
    label: 'Threshold',
    description: 'Branch based on a numeric comparison (e.g., price > 100)',
    icon: '⚖️',
    color: 'border-amber-500/30 bg-amber-500/5',
    category: 'condition',
    defaultConfig: { field: 'price', operator: '>', value: 0 },
  },
  if_else: {
    label: 'If / Else',
    description: 'Branch based on a custom condition expression',
    icon: '🔀',
    color: 'border-amber-500/30 bg-amber-500/5',
    category: 'condition',
    defaultConfig: { condition: '' },
  },

  // Action nodes
  adjust_strategy: {
    label: 'Adjust Strategy',
    description: 'Modify parameters on a running strategy',
    icon: '🔧',
    color: 'border-emerald-500/30 bg-emerald-500/5',
    category: 'action',
    defaultConfig: { strategyId: '', parameters: {} },
  },
  start_stop: {
    label: 'Start / Stop',
    description: 'Start, stop, or pause a strategy',
    icon: '⏯️',
    color: 'border-emerald-500/30 bg-emerald-500/5',
    category: 'action',
    defaultConfig: { strategyId: '', action: 'start' },
  },
  notify: {
    label: 'Notify User',
    description: 'Send a notification message to the user',
    icon: '🔔',
    color: 'border-emerald-500/30 bg-emerald-500/5',
    category: 'action',
    defaultConfig: { message: '' },
  },
};

const handleStyle = {
  width: 8,
  height: 8,
  background: 'hsl(var(--primary))',
  border: '2px solid hsl(240 4% 16%)',
};

export const WorkflowNode = memo(function WorkflowNode({ id, data, selected }) {
  const { label, icon, color, nodeType, config } = data;
  const typeCfg = NODE_TYPES_CONFIG[nodeType] || {};
  const isCondition = typeCfg.category === 'condition';
  const { setNodes, setEdges } = useReactFlow();

  const handleDelete = (e) => {
    e.stopPropagation();
    setNodes((nds) => nds.filter((n) => n.id !== id));
    setEdges((eds) => eds.filter((e) => e.source !== id && e.target !== id));
  };

  return (
    <div
      className={`group relative rounded-lg border px-4 py-3 min-w-[160px] shadow-lg transition-shadow ${color || 'border-white/10 bg-card'} ${
        selected ? 'ring-2 ring-primary shadow-primary/20' : ''
      }`}
    >
      {/* Delete button */}
      <button
        onClick={handleDelete}
        className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500/80 hover:bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
        title="Delete node"
      >
        <X className="w-3 h-3" />
      </button>

      {/* Input handle */}
      {typeCfg.category !== 'input' && (
        <Handle type="target" position={Position.Top} style={handleStyle} />
      )}

      {/* Content */}
      <div className="flex items-center gap-2 mb-1">
        <span className="text-base">{icon}</span>
        <span className="text-xs font-semibold text-foreground">{label}</span>
      </div>

      {/* Config preview */}
      <div className="text-[10px] text-muted-foreground space-y-0.5">
        {nodeType === 'market_data' && config?.symbol && (
          <p>Symbol: {config.symbol}</p>
        )}
        {nodeType === 'timer' && (
          <p>Every {config?.intervalMinutes || 5}m</p>
        )}
        {nodeType === 'threshold' && (
          <p>{config?.field} {config?.operator} {config?.value}</p>
        )}
        {nodeType === 'ai_analysis' && config?.prompt && (
          <p className="truncate max-w-[140px]">{config.prompt}</p>
        )}
        {nodeType === 'adjust_strategy' && config?.strategyId && (
          <p className="truncate max-w-[140px]">Strategy: {config.strategyId.slice(0, 8)}...</p>
        )}
      </div>

      {/* Output handle(s) */}
      {isCondition ? (
        <>
          <Handle
            type="source"
            position={Position.Bottom}
            id="true"
            style={{ ...handleStyle, left: '30%', background: '#22c55e' }}
          />
          <Handle
            type="source"
            position={Position.Bottom}
            id="false"
            style={{ ...handleStyle, left: '70%', background: '#ef4444' }}
          />
        </>
      ) : (
        <Handle type="source" position={Position.Bottom} style={handleStyle} />
      )}
    </div>
  );
});
